import { useEffect, useRef } from "react";

import { DocumentGroup } from "@/api";

import { PendingDocumentMove } from "../DropZoneDnd.types";
import { usePendingDocumentMovesContext } from "../PendingDocumentMovesContext";

interface Params {
  /** The asset inventory zone owned by the mounted view. */
  zone: DocumentGroup;
  /** The view's pending moves (formik field value). */
  moves: PendingDocumentMove[];
  onAddMove: (move: PendingDocumentMove) => void;
  onRemoveMove: (documentId: string) => void;
}

/**
 * Connects an asset inventory view's form to the pending-move store: registers the zone's
 * add/remove handlers (so drops on/from the zone land in the form) and mirrors the form's
 * pending moves into the store so the dropzone panel can render them. Cleans up on unmount,
 * which also covers discarding unsaved changes and navigating away.
 */
export function usePendingMoveZone({ zone, moves, onAddMove, onRemoveMove }: Params) {
  const { registerAssetZone, publishPendingMoves } = usePendingDocumentMovesContext();

  const latestRef = useRef({ onAddMove, onRemoveMove });
  useEffect(() => {
    latestRef.current = { onAddMove, onRemoveMove };
  });

  useEffect(
    () =>
      registerAssetZone(zone, {
        addMove: (move) => latestRef.current.onAddMove(move),
        removeMove: (documentId) => latestRef.current.onRemoveMove(documentId),
      }),
    [registerAssetZone, zone]
  );

  useEffect(() => {
    publishPendingMoves(moves);
  }, [publishPendingMoves, moves]);

  useEffect(() => () => publishPendingMoves([]), [publishPendingMoves]);
}
